import { StandardCommonFieldKey, Zone } from '@/proto/field';
import { TdsFilterType } from '..';
import { TdsFormItemSchema } from './schema';

/**
 * Mock Schema 数据示例
 * 用于测试和演示 TdsSchemaForm 组件
 */

/**
 * Simple 模式示例：最小化配置
 */
export const mockSimpleSchema: TdsFormItemSchema[] = [
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.TimeRange,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AppVersion,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AppVersionMode,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.SdkVersion,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.OsVersion,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.Model,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.Qimei,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.LlmModel,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.BundleId,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AbTestTag,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.ShiplyTag,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.TagsInFilter,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AccountId,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.ProcessName,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.MonitoredThreadName,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.SystemStack,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.NetWorkType,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.GraphicLevelMark,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.Span,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.Area,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.Stage,
  },
];


/**
 * Simple 模式 + 部分覆盖示例
 */
export const mockSimpleSchemaWithOverrides: TdsFormItemSchema[] = [
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AbTestTag,
    overrides: {
      label: '业务下钻标签',
      width: 400,
      hidden: false,
      fieldProps: {
        valueSelectProps: {
          fieldScope: Zone.Lag,
        },
      },
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AccountId,
    overrides: {
      deleteable: false,
      fieldProps: {
        mode: 'tags',
      },
    },
  },
];

/**
 * Full 模式示例：完整自定义配置
 */
export const mockFullSchema: TdsFormItemSchema[] = [
  {
    schemaType: 'full',
    type: 'characterInput',
    name: 'customField1',
    label: '自定义字段1',
    inFilter: true,
    filterType: 'string',
    filterName: 'filterName.自定义字段1',
    fieldProps: {
      mode: 'tags',
      modeOptions: [
        { value: 'OR', label: '是' },
        { value: 'NOT', label: '不是' },
      ],
    },
  },
  {
    schemaType: 'full',
    type: 'characterInput',
    name: 'customField2',
    label: '自定义字段2（使用函数字符串）',
    inFilter: true,
    filterType: 'custom',
    filterName: 'custom_key',
    // 使用函数模板字符串：第一个参数应该是 fieldKey，第二个是 fieldValue，第三个是 key
    filterTransform: '{{ (fieldKey, fieldValue) => customFields2Filter(fieldKey, fieldValue, "a13") }}',
    fieldProps: {
      mode: 'tags',
      modeOptions: [
        { value: 'OR', label: '是' },
        { value: 'NOT', label: '不是' },
      ],
    },
  },
  {
    schemaType: 'full',
    type: 'characterInput',
    name: StandardCommonFieldKey.AppVersion,
    label: 'APP版本（Full模式）',
    labelTools: ['{{ iconTextHtml(\'<span>版本类型筛选项，支持精确搜索，模糊搜索，以及范围搜索，精确搜索和模糊搜索支持输入多个关键字，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
    inFilter: true,
    filterType: 'version',
    filterName: 'filterName.App版本',
    fieldProps: {
      mode: 'tags',
      valueSelectProps: {
        fieldKey: 'app_version',
      },
      modeOptions: [
        { value: 'OR', label: '是' },
        { value: 'NOT', label: '不是' },
      ],
    },
  },
];

/**
 * 混合模式示例：Simple + Full 组合
 */
export const mockMixedSchema: TdsFormItemSchema[] = [
  // Simple 模式：使用基础字段池
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AbTestTag,
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AccountId,
    overrides: {
      width: 300,
    },
  },
  // Full 模式：完全自定义
  {
    schemaType: 'full',
    type: 'characterInput',
    name: 'customBusinessField',
    label: '自定义业务字段',
    inFilter: true,
    filterType: 'custom',
    filterName: 'business_field',
    // 使用函数模板字符串：第一个参数应该是 fieldKey，第二个是 fieldValue，第三个是 key
    filterTransform: '{{ (fieldKey, fieldValue) => customFields2Filter(fieldKey, fieldValue, "b01") }}',
    fieldProps: {
      mode: 'tags',
      modeOptions: [
        { value: 'OR', label: '是' },
        { value: 'NOT', label: '不是' },
      ],
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.SdkVersion,
  },
];

/**
 * 复杂场景示例：包含动态 hidden、字段覆盖等
 */
export const mockComplexSchema: TdsFormItemSchema[] = [
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AppVersion,
    overrides: {
      label: '应用版本',
      width: 350,
      deleteable: false,
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AbTestTag,
    overrides: {
      hidden: false,
      fieldProps: {
        modeOptions: [
          { value: 'OR', label: '包含' },
          { value: 'NOT', label: '不包含' },
        ],
      },
    },
  },
  {
    schemaType: 'full',
    type: 'fieldSelector',
    name: 'customEnumField',
    label: '自定义枚举字段',
    labelTools: ['{{ iconTextHtml(\'<span>枚举类型筛选项，支持输入多个关键字，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
    inFilter: true,
    filterType: 'enumString',
    fieldProps: {
      mode: 'multiple',
      fieldKey: 'custom_enum_key',
    },
  },
];

/**
 * 演示用 overrides 修改 filterName
 * 展示如何通过 Simple Schema 的 overrides 覆盖字段的 filterName 属性
 * 这在需要在保持基础字段配置的同时，自定义过滤键名时非常有用
 */
export const mockFilterKeyOverrideSchema: TdsFormItemSchema[] = [
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AccountId,
    overrides: {
      label: '自定义用户ID',
      // 覆盖 filterName：将默认的 filterName 改为自定义值
      filterName: 'custom_account_id',
      inFilter: true,
      filterType: TdsFilterType.String,
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.BundleId,
    overrides: {
      label: '自定义 Bundle ID',
      // 覆盖 filterName：使用不同的过滤键名
      filterName: 'custom_bundle_id_filter',
      inFilter: true,
      filterType: TdsFilterType.String,
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AbTestTag,
    overrides: {
      label: '业务标签（自定义过滤键）',
      // 覆盖 filterName：修改为业务特定的过滤键名
      filterName: 'business_tag_filter',
      inFilter: true,
      filterType: TdsFilterType.Custom,
    },
  },
];

/**
 * 演示用 overrides 添加 labelTools（ReactNode 提示）
 * 展示如何通过 Simple Schema 的 overrides 添加 labelTools 提示信息
 * labelTools 可以是 ReactNode 数组，用于在字段标签旁显示提示图标或工具
 */
export const mockHintOverrideSchema: TdsFormItemSchema[] = [
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AppVersion,
    overrides: {
      label: 'APP版本（带提示）',
      // 添加 labelTools：使用模板字符串引用提示组件
      // 注意：在实际使用中，labelTools 应该是 ReactNode 数组
      // 这里使用字符串模板，运行时会被解析为实际的 ReactNode
      labelTools: ['{{ iconTextHtml(\'<span>版本类型筛选项，支持精确搜索，模糊搜索，以及范围搜索，精确搜索和模糊搜索支持输入多个关键字，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
      width: 350,
    },
  },
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.SdkVersion,
    overrides: {
      label: 'SDK版本（带提示）',
      labelTools: ['{{ iconTextHtml(\'<span>版本类型筛选项，支持精确搜索，模糊搜索，以及范围搜索，精确搜索和模糊搜索支持输入多个关键字，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
      width: 350,
    },
  },
  {
    schemaType: 'full',
    type: 'fieldSelector',
    name: 'customEnumWithHint',
    label: '自定义枚举字段（带提示）',
    // Full Schema 模式下可以直接使用字符串模板
    labelTools: ['{{ iconTextHtml(\'<span>枚举类型筛选项，支持输入多个关键字，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
    inFilter: true,
    filterType: 'enumString',
    fieldProps: {
      mode: 'multiple',
      fieldKey: 'custom_enum_key',
    },
  },
  {
    schemaType: 'full',
    type: 'text',
    name: 'customStringWithHtmlHint',
    label: '自定义字符串字段（HTML 提示）',
    // 方式1：使用 iconTextHtml 函数，通过模板字符串调用
    labelTools: ['{{ iconTextHtml(\'<span>字符串类型筛选项，支持精确搜索和模糊搜索，详细<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">查看指引</a>。</span>\') }}'],
    inFilter: true,
    filterType: TdsFilterType.String,
  },
  {
    schemaType: 'full',
    type: 'text',
    name: 'customStringWithDirectHtml',
    label: '自定义字符串字段（直接 HTML）',
    // 方式2：直接使用 HTML 字符串，系统会自动检测并解析
    // 注意：这种方式会直接渲染 HTML 内容，但不会显示图标
    // 如果需要图标，请使用方式1的 iconTextHtml
    labelTools: ['<span>这是直接使用 HTML 字符串的示例，支持<a target="_blank" href="https://docs.bugly.woa.com/analysis/search/">链接</a></span>'],
    inFilter: true,
    filterType: TdsFilterType.String,
  },
];

/**
 * 联动协议示例：字段联动控制 disabled 状态和 fieldProps
 * 演示一个字段的值变化后，另一个字段的 disabled 状态和 fieldProps 随之变化
 *
 * 支持两种更新方式：
 * 1. Value 更新（通过 formInstance.setFieldsValue）：
 *    - 仅字段值的更新
 * 2. Schema 层面的更新（通过动态更新 schema 并重新渲染）：
 *    - 除了 value 之外的所有更新都在 Schema 层面处理
 *    - fieldProps.xxx（如 fieldProps.options, fieldProps.mode 等）
 *    - disabled, hidden, required, labelTools, width, deleteable 等所有 TdsFormItemSchema 的属性
 */
export const mockReactionSchema: TdsFormItemSchema[] = [
  // 控制字段：枚举字段选择器，用于选择是否启用
  {
    schemaType: 'full',
    type: 'fieldSelector',
    name: 'enableSwitch',
    label: '启用选项',
    fieldProps: {
      mode: 'single',
      fieldKey: 'enable_status', // 示例枚举字段 key
      options: [
        { value: 'OR', label: '是' },
        { value: 'NOT', label: '不是' },
      ],
    },
  },
  // 被控制字段：当 enableSwitch 为 "OR" 时启用，为 "NOT" 时禁用
  // 同时可以通过 fieldProps 动态更新字段的选项等属性
  {
    schemaType: 'full',
    type: 'fieldSelector',
    name: 'targetField',
    label: '目标字段',
    fieldProps: {
      mode: 'multiple',
      fieldKey: 'target_options', // 示例枚举字段 key
    },
    reactions: [
      {
        dependencies: ['enableSwitch'],
        fulfill: {
          state: {
            // 所有属性除了value，更新都在 Schema 层面，包括 disabled, hidden 等
            disabled: '{{ $deps[0] === "NOT" }}',
            hidden: '{{ $deps[0] === "NOT" }}',
          },
        },
      },
    ],
  },
];

/**
 * 基础字段 + 会话类型示例
 * 包含应用版本、用户ID等基础字段，以及自定义的会话类型字段
 */
export const mockSchema: TdsFormItemSchema[] = [
  // 应用版本：使用 Simple Schema
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AppVersion,
  },
  // 用户ID：使用 Simple Schema
  {
    schemaType: 'simple',
    name: StandardCommonFieldKey.AccountId,
  },
  // 会话类型：使用 Full Schema，自定义字段，filterName 为 sessionType
  {
    schemaType: 'full',
    type: 'fieldSelector',
    name: 'sessionType',
    label: '会话类型',
    inFilter: true,
    filterType: 'enumString',
    filterName: 'sessionType',
    fieldProps: {
      mode: 'tags',
      fieldKey: 'session_type',
    },
  },
];

